import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  CloseButton,
  Col,
  Container,
  Form,
  InputGroup,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";
import {
  ArrowCounterclockwise,
  ChevronExpand,
  Plus,
  Trash,
} from "react-bootstrap-icons";

interface ModelInfoArrayEditModalProps {
  columnName: string;
  allModelVariant?: boolean;
  currentValues: (string | number)[];
  onlyAllowNumbers: boolean;
  allowDecimals: boolean;
  disabled: boolean;
  isSortable: boolean;
  onCancel: (newArray: (string | number)[], isAllModelVariant: boolean) => void;
}

function ModelInfoArrayEditModal({
  columnName,
  allModelVariant = false,
  currentValues,
  onlyAllowNumbers,
  allowDecimals,
  disabled,
  isSortable = false,
  onCancel,
}: ModelInfoArrayEditModalProps) {
  const [show, setShow] = useState(true);
  const [showAlert, setShowAlert] = useState(true);
  const [valueToAdd, setValueToAdd] = useState<string>("");
  const [alertMessage, setAlertMessage] = useState<string | undefined>(
    undefined,
  );
  const [isAllModelVariant, setIsAllModelVariant] =
    useState<boolean>(allModelVariant);
  const [values, setValues] = useState(currentValues);
  const [sort, setSort] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    if (
      !values.includes("") &&
      new Set(values.map((value) => (value = value.toString().toLowerCase())))
        .size === values.length
    ) {
      setShow(false);
      onCancel(values, isAllModelVariant);
    } else {
      setAlertMessage("Remove duplicates or empty entries from this list.");
    }
  };

  const trimModelInfoValue = (value: string): string => {
    if (value === undefined) return "";
    return value.replace(/\s+/g, " ").trim();
  };

  const addItemToArray = () => {
    setAlertMessage(undefined);

    if (!values) {
      setValues([valueToAdd]);
    } else {
      const index = values.findIndex((item) => {
        if (typeof item === "string") {
          return (
            item.toLowerCase() === trimModelInfoValue(valueToAdd.toLowerCase())
          );
        } else {
          return item === parseFloat(valueToAdd);
        }
      });

      if (index !== -1) {
        setAlertMessage("The given value already exists in this list.");
      } else {
        setValues([...values, valueToAdd]);
      }
    }

    if (inputRef.current) {
      inputRef.current.focus();
    }

    setValueToAdd("");
  };

  const removeItemFromArray = (indexToRemove: number) => {
    setValues(values.filter((value, index) => index !== indexToRemove));
  };

  const updateArray = (newValue: string | number, index: number) => {
    let updatedValues = [...values];
    updatedValues[index] = newValue;

    setValues(updatedValues);
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && valueToAdd) {
      addItemToArray();
    }
  };

  const revertChanges = () => {
    setValues(currentValues);
    setAlertMessage(undefined);
  };

  const sortArray = (order: boolean) => {
    const sortedArray = [...values];

    if (!onlyAllowNumbers) {
      sortedArray.sort((a, b) =>
        a !== undefined && b !== undefined
          ? order === false
            ? b.toString().toUpperCase().localeCompare(a.toString())
            : a.toString().toUpperCase().localeCompare(b.toString())
          : 0,
      );
    } else if (onlyAllowNumbers) {
      sortedArray.sort((a, b) =>
        order === false
          ? parseInt(b.toString()) - parseInt(a.toString())
          : parseInt(a.toString()) - parseInt(b.toString()),
      );
    }

    setValues(sortedArray);
  };

  return (
    <>
      <Modal show={show} onHide={handleClose}>
        <Modal.Header>
          <Modal.Title
            className="array-edit-modal-title"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
            onClick={() => {
              sortArray(sort);

              setSort(!sort);
            }}
          >
            {disabled ? "View" : "Edit"} {columnName}
            {isSortable && (
              <ChevronExpand
                className="text-primary"
                style={{
                  display: "flex",
                  margin: "1px",
                  borderLeft: "1px solid #0d6efd",
                  marginLeft: "5px",
                }}
              />
            )}
          </Modal.Title>
          {!disabled && columnName === "modelVariants" && (
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id="tooltip-bottom">
                  Enables <strong>all</strong> model variants.
                </Tooltip>
              }
              delay={{ show: 250, hide: 150 }}
            >
              <Form.Check
                style={{ marginLeft: "15px", paddingTop: "0px" }}
                type="switch"
                checked={isAllModelVariant}
                id="all-model-variant-switch"
                onChange={(e) => setIsAllModelVariant(e.target.checked)}
              />
            </OverlayTrigger>
          )}
          <CloseButton onClick={handleClose} />
        </Modal.Header>
        <Modal.Body>
          {isAllModelVariant && values.length > 0 && (
            <Alert
              variant="warning"
              onClose={() => setShowAlert(!showAlert)}
              dismissible
            >
              <Row>
                <Col xl={11}>
                  Saving <strong>all</strong> model variants will disable
                  current values.
                </Col>
              </Row>
            </Alert>
          )}
          {alertMessage !== undefined && (
            <Alert variant="warning">
              <Row>
                <Col xl={11}>{alertMessage}</Col>
                <Col xl={1}>
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Tooltip id="tooltip-bottom">
                        Reverts <strong>all</strong> changes.
                      </Tooltip>
                    }
                    delay={{ show: 250, hide: 150 }}
                  >
                    <ArrowCounterclockwise
                      type="button"
                      onClick={revertChanges}
                    />
                  </OverlayTrigger>
                </Col>
              </Row>
            </Alert>
          )}
          <Container
            style={{ paddingTop: "5px", maxHeight: 500, overflowY: "auto" }}
          >
            <Row>
              <Col>
                {!disabled && (
                  <InputGroup className="mb-3">
                    <Form.Control
                      disabled={isAllModelVariant}
                      onChange={(e) => {
                        onlyAllowNumbers
                          ? allowDecimals
                            ? setValueToAdd(
                                e.target.value.replace(/[^0-9.]/g, ""),
                              )
                            : setValueToAdd(
                                e.target.value.replace(/[^0-9]/g, ""),
                              )
                          : setValueToAdd(e.target.value);
                      }}
                      value={valueToAdd}
                      ref={inputRef}
                      onKeyDown={handleKeyPress}
                    ></Form.Control>
                    <Button
                      variant="success"
                      disabled={valueToAdd === ""}
                      onClick={() => {
                        addItemToArray();
                      }}
                    >
                      <Plus width={20} height={20}></Plus>
                    </Button>
                  </InputGroup>
                )}
              </Col>
            </Row>
            {values &&
              !isAllModelVariant &&
              values.map((value, index) => (
                <ArrayItemEdit
                  index={index}
                  key={index}
                  value={value}
                  values={values}
                  allowDecimals={allowDecimals}
                  onlyAllowNumbers={onlyAllowNumbers}
                  disabled={disabled || isAllModelVariant}
                  updateArray={updateArray}
                  removeItemFromArray={removeItemFromArray}
                />
              ))}
          </Container>
        </Modal.Body>
      </Modal>
    </>
  );
}

interface ArrayItemEditProps {
  index: number;
  value: string | number;
  values: (string | number)[];
  onlyAllowNumbers: boolean;
  allowDecimals: boolean;
  disabled: boolean;
  updateArray: (newValue: string | number, index: number) => void;
  removeItemFromArray: (indexToRemove: number) => void;
}

function ArrayItemEdit({
  index,
  value,
  values,
  onlyAllowNumbers,
  allowDecimals,
  disabled,
  updateArray,
  removeItemFromArray,
}: ArrayItemEditProps) {
  return (
    <Row>
      <Col>
        <InputGroup className="mb-3">
          <Form.Control
            key={index}
            value={value}
            disabled={disabled}
            onChange={(e) => {
              updateArray(
                onlyAllowNumbers
                  ? allowDecimals
                    ? e.target.value.replace(/[^0-9.]/g, "")
                    : e.target.value.replace(/[^0-9]/g, "")
                  : e.target.value,
                index,
              );
            }}
            isInvalid={
              value === "" ||
              values.filter(
                (item) =>
                  item.toString().toLowerCase() ===
                  value.toString().toLowerCase(),
              ).length > 1
                ? true
                : false
            }
          />
          {!disabled && (
            <Button variant="danger" onClick={() => removeItemFromArray(index)}>
              <Trash width={20} height={20} />
            </Button>
          )}
        </InputGroup>
      </Col>
    </Row>
  );
}

export default ModelInfoArrayEditModal;
